import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  StorageNativeStoreAction,
  TimeSlotStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { TimeslotService } from "src/service/timeslot.service";
import {
  groupTimeSLotLoadErrorAction,
  groupTimeSlotLoadLastModifiedFromApiAction,
  groupTimeSlotLoadListFromApiAction,
  groupTimeSlotLoadSuccessAction,
  groupTimeSlotUnsetKeyStorage,
  timeSlotListPopulateWithOldDataAction,
  timeSLotLoadErrorAction,
  timeSlotLoadErrorHandlingAction,
  timeSlotLoadLastModifiedFromApiAction,
  timeSlotLoadListFromApiAction,
  timeSlotLoadSuccessAction,
  timeSlotUnsetKeyStorage,
} from "../actions/time-slot.action";
import { currentUserData } from "../selectors/authentication.selectors";
import {
  listGroupTimeSlot,
  listTimeSlot,
} from "../selectors/time-slot.selector";

/**
 * Handle generic side ngrx effect from timeslot/ group time slot action
 */
@Injectable()
export class TimeSlotEffects {
  constructor(
    private _actions$: Actions,
    private _timeSlotService: TimeslotService,
    private _store: Store
  ) { }

  //#region Time Slot
  /**
   * Effect who catch main event and unset specific server key storage
   */
  public timeSlotUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      switchMap((action) => [
        timeSlotUnsetKeyStorage(),
        groupTimeSlotUnsetKeyStorage(),
      ])
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public timeSlotStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listTimeSlot)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const timeSlotObject: GenericStoreApiData = combinedObject[2];

        if (timeSlotObject?.data || timeSlotObject?.isOldDataInUse) {
          return timeSlotLoadLastModifiedFromApiAction({
            timeSlotObject: timeSlotObject.data,
          });
        } else {
          return timeSlotLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of time slot effect
   */
  public timeSlotListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._timeSlotService.getTimeslot().pipe(
          map((timeSlotObject: GenericApiData) =>
            timeSlotLoadSuccessAction({ timeSlotObject })
          ),
          catchError((error: HttpErrorResponse) =>
            of(timeSlotLoadErrorHandlingAction(error))
          )
        );
      })
    )
  );

  /**
   * Load full list of time slot by last modified effect
   */
  public timeSlotListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._timeSlotService
          .getTimeslotByLastModify(action.timeSlotObject)
          .pipe(
            map((timeSlotObject: GenericApiData) =>
              timeSlotLoadSuccessAction({ timeSlotObject })
            ),
            catchError((error: HttpErrorResponse) =>
              of(timeSlotLoadErrorHandlingAction(error))
            )
          );
      })
    )
  );

  /**
   * Effect handling error in valid list loading
   */
  public timeSlotListStartErrorHandlingEffects = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_LOAD_ERROR_HANDLING),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listTimeSlot)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      switchMap((action) => {
        const httpError: HttpErrorResponse = action[0] as HttpErrorResponse;
        const timeSlotListStore: GenericStoreApiData = action[2];

        if (
          timeSlotListStore?.loaded &&
          !timeSlotListStore.errorGeneric &&
          !timeSlotListStore.errorNotYetImplemented
        ) {
          return [
            timeSlotListPopulateWithOldDataAction({
              oldTimeSlotObject: timeSlotListStore.data,
            }),
          ];
        } else {
          return [timeSLotLoadErrorAction()];
        }
      })
    )
  );

  //#endregion

  //#region Group Time Slot

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public groupTimeSlotStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_GROUP_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listGroupTimeSlot)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const timeSlotGroupObject: GenericStoreApiData = combinedObject[2];

        if (timeSlotGroupObject?.data) {
          return groupTimeSlotLoadLastModifiedFromApiAction({
            groupTimeSlotObject: timeSlotGroupObject.data,
          });
        } else {
          return groupTimeSlotLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of group time slot effect
   */
  public groupTimeSlotListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_GROUP_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._timeSlotService.getGroupTimeSlot().pipe(
          map((groupTimeSlotObject: GenericApiData) =>
            groupTimeSlotLoadSuccessAction({ groupTimeSlotObject })
          ),
          catchError(() => of(groupTimeSLotLoadErrorAction()))
        );
      })
    )
  );

  /**
   * Load full list of grouè time slot by last modified effect
   */
  public groupTimeSlotListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(TimeSlotStoreAction.TIMESLOT_GROUP_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._timeSlotService
          .getGroupTimeSlotByLastModify(action.groupTimeSlotObject)
          .pipe(
            map((groupTimeSlotObject: GenericApiData) =>
              groupTimeSlotLoadSuccessAction({ groupTimeSlotObject })
            ),
            catchError(() => of(groupTimeSLotLoadErrorAction()))
          );
      })
    )
  );
  //#endregion
}
